import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { usePerson } from '@/account/usePerson';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { LoadingScreen } from '@/components/LoadingScreen/LoadingScreen';
import { ApiKeyContainer } from '@/containers/ApiKeyContainer';
import { ApiKeysContainer } from '@/containers/ApiKeysContainer';
import { CompleteAccountContainer } from '@/containers/CompleteAccountContainer';
import { HomeContainer } from '@/containers/HomeContainer';
import { JobContainer } from '@/containers/JobContainer';
import { LiveJobContainer } from '@/containers/LiveJobContainer';
import { NewJobContainer } from '@/containers/NewJobContainer';
import { PlaygroundContainer } from '@/containers/PlaygroundContainer';
import { SettingsContainer } from '@/containers/SettingsContainer';
import { SettingsLayoutContainer } from '@/containers/SettingsLayoutContainer';
import { SignInContainer } from '@/containers/SignInContainer';

/**
 * One route per page. Add a `<Route>` here and a container under
 * `src/containers/` — that pair is the whole recipe for a new page.
 * Every page past sign-in needs an Arcade Person, so they get one first.
 * Signed out, every path is `/`; sign-in returns to the path that was asked for.
 */
export function AppRoutes() {
  const { status, signOut } = useAuth();
  const { person, loading, error, refetch } = usePerson();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from;

  if (status === 'restoring') {
    return <LoadingScreen message="Restoring session…" />;
  }

  if (status === 'anonymous') {
    if (location.pathname !== '/') {
      const from = location.pathname + location.search;
      return <Navigate to="/" replace state={{ from }} />;
    }
    return <SignInContainer />;
  }

  if (loading) {
    return <LoadingScreen message="Loading your account…" />;
  }

  if (error) {
    return (
      <Callout tone="danger" title="Could not load your account">
        <p>The developer platform did not answer.</p>
        <Button variant="ghost" onClick={refetch}>
          Try again
        </Button>
        <Button variant="ghost" onClick={signOut}>
          Sign out
        </Button>
      </Callout>
    );
  }

  if (!person) {
    return <CompleteAccountContainer />;
  }

  if (returnTo) {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomeContainer person={person} />} />
      <Route path="/playground" element={<PlaygroundContainer />} />
      <Route path="/playground/new" element={<NewJobContainer />} />
      <Route path="/playground/live" element={<LiveJobContainer />} />
      <Route path="/playground/:id" element={<JobContainer />} />
      <Route path="/papermill" element={<Navigate to="/playground" replace />} />
      <Route path="/papermill/:id" element={<PapermillRedirect />} />
      <Route path="/settings" element={<SettingsLayoutContainer />}>
        <Route index element={<SettingsContainer person={person} />} />
        <Route path="api-keys" element={<ApiKeysContainer />} />
        <Route path="api-keys/:id" element={<ApiKeyContainer />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Links from before the playground held more than Papermill. */
function PapermillRedirect() {
  const { id = '' } = useParams();
  return <Navigate to={`/playground/${id}`} replace />;
}
